import React from 'react';
import { motion } from 'framer-motion';
import { ChevronRight } from 'lucide-react';
import heroImage from '../images/hero.jpg'; // ✅ local hero image

const companyName = 'Sudharma Clothing Company Pvt. Ltd.';

// Variants for each letter of the animated name
const letterVariants = {
  hidden: { opacity: 0, y: 6 },
  visible: { opacity: 1, y: 0 },
};

const Hero = () => {
  return (
    <section
      id="home"
      className="relative h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Animated background image */}
      <motion.div
        className="absolute inset-0 bg-cover bg-center"
        style={{ backgroundImage: `url(${heroImage})` }}
        initial={{ scale: 1.05 }}
        animate={{ scale: [1.05, 1.12, 1.05] }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/45 z-10" />

      {/* Content */}
      <div className="relative z-20 text-center max-w-4xl mx-auto px-4 text-white">
        {/* Animated company name pill */}
        <motion.span
          className="inline-block px-4 py-1 mb-4 text-xs tracking-[0.25em] uppercase bg-white/10 border border-white/20 rounded-full"
          initial="hidden"
          animate="visible"
          variants={{
            visible: {
              transition: {
                staggerChildren: 0.01, // speed of letters
              },
            },
          }}
        >
          {companyName.split('').map((char, index) => (
            <motion.span key={index} variants={letterVariants}>
              {char === ' ' ? '\u00A0' : char}
            </motion.span>
          ))}
        </motion.span>

        <h1 className="text-4xl md:text-6xl font-playfair font-bold mb-6">
          Premium Garment Manufacturer
        </h1>

        <p className="text-lg md:text-2xl mb-8 max-w-3xl mx-auto">
          Specializing in T-shirts, sportswear and winter wear with precise
          fabrication, reliable delivery and high-end finishing quality.
        </p>

        {/* CTA buttons */}
        <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
          <a
            href="#products"
            className="group inline-flex items-center gap-2 rounded-full bg-amber-400 text-gray-900 px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base font-semibold shadow-lg shadow-amber-500/30 hover:bg-amber-300 hover:-translate-y-0.5 hover:shadow-xl transition-transform transition-colors"
          >
            <span>Explore Products</span>
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5 transition-transform group-hover:translate-x-0.5" />
          </a>

          <a
            href="#sample-room"
            className="inline-flex items-center gap-2 rounded-full border border-white/40 bg-white/5 px-6 md:px-8 py-2.5 md:py-3 text-sm md:text-base font-medium text-white hover:bg-white/15 hover:border-white transition-colors"
          >
            View Sample Room
          </a>
        </div>
      </div>

      {/* Floating WhatsApp button */}
      <motion.a
        href="https://wa.me/919999999999?text=Hello%20Sudharma%20Clothing%2C%20I%20would%20like%20to%20know%20more%20about%20your%20products."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-5 right-5 z-50 flex items-center justify-center w-14 h-14 rounded-full bg-green-500 shadow-lg hover:bg-green-600 transition-colors"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.3 }}
      >
        <i className="fa-brands fa-whatsapp text-white text-2xl" />
      </motion.a>
    </section>
  );
};

export default Hero;
